import React, { useMemo, useContext } from 'react';

import { IndexGridNew } from '@rockerbox/styleguide';
import { useAtom } from 'jotai';
import { Header } from 'semantic-ui-react';

import { Charts, SpendEffectChart, ROIHatChart } from './charts';
import { createTierColorMap } from './helpers';
import { useTableCalculations } from './hooks/useChartAndTable';
import { useDates } from './hooks/useDates';
import { useTrainingData } from './hooks/useTrainingData';
import MediaVsBaselineChart from './MediaVsBaselineChart';
import { kpiAtom } from '../../../atoms';
import { FeatureAliasesContext } from '../../../context/MMMFeatureAliases';
import { useGlobalState } from '../../../hooks/global';
import MMMDateRangeHooks from '../parts/MMMDateRangeHooks';

const MarketingPerformance = ({ rawData, trainingInfo, responseCurves }) => {
  const { tierColors } = useGlobalState();
  const [kpi] = useAtom(kpiAtom);
  const isConversionKPI = kpi === 'conversions';
  const { trainingData, trainingDates } = trainingInfo;
  const { roi_hat: featureROI, spend_effect: spendEffectData } = rawData;

  const { featureAliasesMap: featureNameMap } = useContext(FeatureAliasesContext);
  const { startDate, endDate, onDateChange, dateRange } = useDates(trainingDates);

  const {
    selectedData,
    selectedRevData: selectedKPIData,
    selectedROI: selectedMetric,
    timeSeriesDataByDateRange,
  } = useTrainingData(isConversionKPI, trainingData, responseCurves, featureNameMap, startDate, endDate);

  const tierColorMap = createTierColorMap(tierColors, featureNameMap);
  const { cols, tableData } = useTableCalculations(timeSeriesDataByDateRange, isConversionKPI, tierColorMap);
  const chartData = { selectedData, selectedMetric, timeSeriesDataByDateRange, selectedKPIData };

  const mediaVsBaseline = useMemo(() => {
    if (!rawData || !rawData.media_vs_baseline) return [];
    const { media_vs_baseline } = rawData;
    return media_vs_baseline.map(d => (
      {
        date: d.date,
        baselineContribution: d.baseline_contribution,
        mediaContribution: d.media_contribution,
        baselinePct: (d.baseline_contribution / (d.baseline_contribution + d.media_contribution)) * 100,
        mediaPct: (d.media_contribution / (d.baseline_contribution + d.media_contribution)) * 100,
      }
    ));
  }, [rawData]);

  return (
    <>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 3 }}>
          <Header as="h2" style={{ margin: '1rem', marginTop: '1.5rem' }}>
            Performance Overview
          </Header>
          <Header as="h5" style={{ margin: '1rem' }}>
            Below is an overview of performance, as measured and evaluated using the Marketing Mix Model.
          </Header>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '1rem', margin: '1rem', maxWidth: '300px' }}>
            {startDate && (
              <MMMDateRangeHooks {...{
                startDate, endDate, onDateChange, dateRange, options: false, localStorageKey: 'marketingPerformanceDateRange', trackName: 'mmm.marketing_performance.date_selector',
              }}
              />
            )}
          </div>
        </div>
      </div>
      {!!trainingData && (
        <>
          <IndexGridNew
            data={tableData}
            cols={cols}
            sortable
          />
          <MediaVsBaselineChart {...{ mediaVsBaseline, isConversionKPI }} />
          <Charts {...{ isConversionKPI, chartData, featureNameMap, tierColorMap }} />
        </>
      )}
      {spendEffectData && spendEffectData.length > 0 && (
        <SpendEffectChart
          spendEffectData={spendEffectData}
          isConversionKPI={isConversionKPI}
          tierColorMap={tierColorMap}
          featureNameMap={featureNameMap}
        />
      )}
      {true && (
        <ROIHatChart ROIHatData={featureROI} {...{ isConversionKPI, tierColorMap, featureNameMap }} />
      )}
    </>
  );
};

export default MarketingPerformance;
